import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Layers, 
  Upload, 
  HelpCircle, 
  CreditCard, 
  User, 
  LogOut, 
  Menu, 
  X,
  Moon,
  Sun,
  Globe,
  Home,
  ScanSearch,
  Chrome,
  Sparkles,
  FileSpreadsheet,
  Users,
  Building2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { path: '/', label: t('nav_home'), icon: Home },
    { path: '/generator', label: language === 'nl' ? 'Generator' : 'Generator', icon: Sparkles },
    { path: '/scan', label: language === 'nl' ? 'Scannen' : 'Scan', icon: ScanSearch },
    { path: '/bulk', label: t('nav_bulk'), icon: Layers, pro: true },
    { path: '/bank', label: t('nav_bank'), icon: Upload, pro: true },
    { path: '/mt940', label: 'PDF→MT940', icon: FileSpreadsheet, pro: true },
    { path: '/btw-check', label: 'BTW Check', icon: Building2 },
    { path: '/uitleg', label: t('nav_uitleg'), icon: HelpCircle },
    { path: '/pricing', label: t('nav_pricing'), icon: CreditCard },
    { path: '/extension', label: language === 'nl' ? 'Extensie' : 'Extension', icon: Chrome },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50 dark:border-slate-700/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <span className="font-heading font-bold text-xl text-primary dark:text-white">
              Kenmerk<span className="text-accent">Check.nl</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(({ path, label, icon: Icon, pro }) => (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${path.replace('/', '') || 'home'}`}
                className={`
                  relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm
                  transition-colors
                  ${isActive(path) 
                    ? 'text-accent bg-accent/10' 
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'}
                `}
              >
                <Icon className="w-4 h-4" />
                {label}
                {pro && (
                  <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-bold bg-accent text-white rounded-full">
                    PRO
                  </span>
                )}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Language toggle */}
            <button
              onClick={toggleLanguage}
              className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              data-testid="language-toggle"
              aria-label="Toggle language"
            >
              <Globe className="w-5 h-5" />
              <span className="sr-only">{language.toUpperCase()}</span>
            </button>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Auth buttons */}
            <div className="hidden md:flex items-center gap-2">
              {isAuthenticated ? (
                <>
                  {user?.plan === 'team' && (
                    <Link
                      to="/team"
                      data-testid="nav-team"
                      className={`
                        flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm
                        ${isActive('/team')
                          ? 'text-accent bg-accent/10'
                          : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'}
                      `}
                    >
                      <Users className="w-4 h-4" />
                      Team
                    </Link>
                  )}
                  <Link
                    to="/dashboard"
                    data-testid="nav-dashboard"
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm
                      ${isActive('/dashboard')
                        ? 'text-accent bg-accent/10'
                        : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'}
                    `}
                  >
                    <User className="w-4 h-4" />
                    {user?.name || t('nav_dashboard')}
                    {user?.plan !== 'free' && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-accent to-blue-600 text-white rounded uppercase">
                        {user?.plan}
                      </span>
                    )}
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="logout-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm text-slate-600 dark:text-slate-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    data-testid="nav-login"
                    className="px-4 py-2 rounded-lg font-medium text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    {t('nav_login')}
                  </Link>
                  <Link
                    to="/register"
                    data-testid="nav-register"
                    className="px-4 py-2 rounded-lg font-medium text-sm bg-primary dark:bg-white text-white dark:text-primary hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors btn-press"
                  >
                    {t('nav_register')}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-slate-200/50 dark:border-slate-700/50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl"
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map(({ path, label, icon: Icon, pro }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setMobileOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg font-medium
                    ${isActive(path)
                      ? 'text-accent bg-accent/10'
                      : 'text-slate-600 dark:text-slate-300'}
                  `}
                >
                  <Icon className="w-5 h-5" />
                  {label}
                  {pro && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-accent text-white rounded-full">
                      PRO
                    </span>
                  )}
                </Link>
              ))}
              
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                {isAuthenticated ? (
                  <>
                    {user?.plan === 'team' && (
                      <Link
                        to="/team"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-slate-600 dark:text-slate-300"
                      >
                        <Users className="w-5 h-5" />
                        Team beheren
                      </Link>
                    )}
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-slate-600 dark:text-slate-300"
                    >
                      <User className="w-5 h-5" />
                      {user?.name || t('nav_dashboard')}
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setMobileOpen(false); }}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-red-600 w-full"
                    >
                      <LogOut className="w-5 h-5" />
                      {t('nav_logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-slate-600 dark:text-slate-300"
                    >
                      {t('nav_login')}
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-primary text-white"
                    >
                      {t('nav_register')}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
